import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    RootState,
    useAppSelector,
} from '../../redux';
import { Change } from '../../models';
import { Changes } from '../../services';
import { ChangeCard, ChangeModal, Icon, WidgetSkeleton } from '../../components';
import { useDashboardChangeModal } from '../../utils/useDashboardChangeModal';

const DISPLAY_LIMIT = 8;

function WhatsNew() {
    const { t } = useTranslation();
    const { user } = useAppSelector((state: RootState) => state.auth);
    const {
        modalChangeId,
        detailChange,
        openChangeModal,
        closeChangeModal,
    } = useDashboardChangeModal();

    const [items, setItems] = useState<Change[]>([]);
    const [loading, setLoading] = useState(true);

    const loadItems = useCallback(() => {
        const userId = user?._id;
        if (!userId) {
            setItems([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        Changes.list({ assignee: userId, limit: DISPLAY_LIMIT })
            .then((json) => {
                const changes = (json.changes || []) as Change[];
                const sorted = [...changes].sort((a, b) => {
                    const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    return bTime - aTime;
                });
                setItems(sorted.slice(0, DISPLAY_LIMIT));
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [user?._id]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const onRemoveCard = () => {
        closeChangeModal();
        loadItems();
    };

    if (loading)
        return <WidgetSkeleton rows={4} />;

    if (!items.length)
        return null;

    return (
        <div className="WhatsNew rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-end justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <h3 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
                    <Icon type="activity" size={6} className="mr-1.5" />
                    {t('dashboard.whats_new')}
                    <span className="ml-1 text-base font-normal text-gray-500 dark:text-gray-400">({items.length})</span>
                </h3>
            </div>
            <div className="max-h-[450px] space-y-1.5 overflow-y-auto p-4">
                {items.map((change: Change) => (
                    <ChangeCard
                        key={change._id}
                        change={change}
                        showProject
                        showUpdatedAt
                        className="bg-indigo-50 dark:bg-indigo-950/30/40 dark:bg-indigo-950/20"
                        onClick={() => openChangeModal(change)}
                    />
                ))}
            </div>

            {detailChange && detailChange._id === modalChangeId && detailChange.project?.name && (
                <ChangeModal
                    project={detailChange.project}
                    change={detailChange}
                    show={true}
                    showProjectName={true}
                    onClose={closeChangeModal}
                    onRemove={onRemoveCard}
                />
            )}
        </div>
    );
}

export default WhatsNew;
